/**
 * WebsiteMenusPanel.js — Phase E
 * AD-facing UI to manage WordPress nav menus + items via the OL bridge,
 * without ever logging into WP-admin.
 *
 *   • Left column: list of menus, "+ New" button, click a row to open it.
 *   • Right column (when a menu is open): rename, delete, manage items
 *     (add / rename / re-URL / re-order / delete).
 *
 * All writes go through `/api/wp-menus/{org_id}` which proxies the
 * `ol-bridge/v1/menus` controller and unwraps the Envelope shape.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LiveSiteHeader from './LiveSiteHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebsiteMenusPanel({ orgId }) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bridgeMissing, setBridgeMissing] = useState(false);
  const [wpUnreachable, setWpUnreachable] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const loadMenus = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setBridgeMissing(false); setWpUnreachable(false);
    try {
      const { data } = await axios.get(`${API}/wp-menus/${orgId}`, { withCredentials: true });
      setMenus(data.menus || []);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 503 && /wp_unreachable/.test(detail)) {
        setWpUnreachable(true);
      } else if (e.response?.status === 502 && /bridge_lacks_menus/.test(detail)) {
        setBridgeMissing(true);
      }
      setMenus([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const [openMenuKind, setOpenMenuKind] = useState('classic');

  const loadMenu = useCallback(async (menuId, kind = 'classic') => {
    if (!menuId) { setOpenMenu(null); return; }
    try {
      const { data } = await axios.get(`${API}/wp-menus/${orgId}/${menuId}?kind=${kind}`, { withCredentials: true });
      setOpenMenu({ ...data, kind });
    } catch {
      setOpenMenu(null);
    }
  }, [orgId]);

  useEffect(() => { loadMenus(); }, [loadMenus]);
  useEffect(() => { loadMenu(openMenuId, openMenuKind); }, [openMenuId, openMenuKind, loadMenu]);

  const openRow = (m) => {
    setOpenMenuKind(m.kind || 'classic');
    setOpenMenuId(m.id);
  };

  const createMenu = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/wp-menus/${orgId}?kind=classic`,
        { name: newName.trim() },
        { withCredentials: true }
      );
      setNewName('');
      await loadMenus();
      if (data?.id) { setOpenMenuKind('classic'); setOpenMenuId(data.id); }
    } finally {
      setCreating(false);
    }
  };

  const renameMenu = async (name) => {
    await axios.patch(`${API}/wp-menus/${orgId}/${openMenuId}?kind=${openMenuKind}`,
                       { name }, { withCredentials: true });
    await loadMenus();
    await loadMenu(openMenuId, openMenuKind);
  };

  const deleteMenu = async () => {
    if (!window.confirm('Delete this menu? Items will be removed from WordPress.')) return;
    await axios.delete(`${API}/wp-menus/${orgId}/${openMenuId}?kind=${openMenuKind}`, { withCredentials: true });
    setOpenMenuId(null);
    setOpenMenu(null);
    await loadMenus();
  };

  if (wpUnreachable) {
    return (
      <div data-testid="wp-menus-wp-unreachable" className="p-6 text-sm">
        <div className="p-4 rounded bg-red-50 border border-red-200 text-red-900">
          <strong>WordPress site unreachable.</strong>
          <p className="mt-1">
            The connected WP site is not responding (ol-bridge <code>/health</code>
            also returns 404). If you&apos;re pointing at a Flywheel Local site,
            the Live Link tunnel has likely expired — open Local, toggle the
            Live Link off and back on, then reload this panel. The bridge
            plugin status is not the problem — the entire site is offline.
          </p>
          <button
            data-testid="wp-menus-retry"
            onClick={loadMenus}
            className="mt-3 px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
          >Retry</button>
        </div>
      </div>
    );
  }

  if (bridgeMissing) {
    return (
      <div data-testid="wp-menus-bridge-missing" className="p-6 text-sm">
        <div className="p-4 rounded bg-amber-50 border border-amber-200 text-amber-900">
          <strong>WordPress bridge plugin needs update.</strong>
          <p className="mt-1">
            The WP site is responding, but the ol-bridge plugin running there
            does not expose <code>/menus</code>. Ship the <code>MenusController</code>
            module to that site (see <code>/docs/wp-bridge-v3.4/routes-menus.php</code>)
            and reload this panel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="website-menus-panel" className="p-6 space-y-4">
      <LiveSiteHeader orgId={orgId} surfaceLabel="Menus" />
      <HeaderAppearance orgId={orgId} />
      <div className="grid grid-cols-12 gap-6">
      {/* Left — menu list */}
      <div className="col-span-4">
        <h2 className="text-lg font-semibold mb-3">WordPress Menus</h2>
        <div className="flex gap-2 mb-4">
          <input
            data-testid="wp-menus-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New menu name"
            className="flex-1 border rounded px-3 py-2 text-sm"
          />
          <button
            data-testid="wp-menus-create"
            onClick={createMenu}
            disabled={creating || !newName.trim()}
            className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
          >+ Add</button>
        </div>

        {loading && <div className="text-gray-500 text-sm">Loading…</div>}
        {!loading && menus.length === 0 && (
          <div className="text-sm py-3 px-3 rounded bg-amber-50 border border-amber-200 text-amber-900 space-y-2" data-testid="wp-menus-empty">
            <div className="font-medium">No classic menus on this WP site.</div>
            <p className="text-xs leading-relaxed">
              Modern WordPress block themes (Twenty Twenty-Four, Twenty Twenty-Five, etc.) store
              navigation in <code>wp_navigation</code> block posts instead of the legacy
              <code> wp_nav_menu()</code> system. Your site uses block navigation — that&apos;s why
              this list is empty even though your header / footer nav is visible.
            </p>
            <p className="text-xs leading-relaxed">
              <strong>Options:</strong>
            </p>
            <ul className="text-xs leading-relaxed list-disc pl-5 space-y-1">
              <li>Edit nav directly in wp-admin → Appearance → Editor → Navigation (for now)</li>
              <li>Ask the bridge plugin team to expose <code>/navigation</code> — spec at
                  <code> docs/wp-bridge-extensions/BLOCK_NAVIGATION.md</code>; once that ships
                  this panel will manage block-nav too</li>
              <li>Or create a classic menu here — it&apos;ll save into WP but won&apos;t render
                  until you add <code>wp_nav_menu()</code> in the theme</li>
            </ul>
          </div>
        )}
        <ul className="space-y-1" data-testid="wp-menus-list">
          {menus.map((m) => (
            <li key={`${m.kind || 'classic'}-${m.id}`}>
              <button
                data-testid={`wp-menus-row-${m.id}`}
                onClick={() => openRow(m)}
                className={`w-full text-left px-3 py-2 rounded text-sm ${
                  openMenuId === m.id && openMenuKind === (m.kind || 'classic')
                    ? 'bg-blue-100 font-medium' : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{m.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    m.kind === 'block'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-200 text-gray-700'
                  }`}>{m.kind === 'block' ? 'block-nav' : 'classic'}</span>
                </div>
                <div className="text-xs text-gray-500">slug: {m.slug} · {m.count || 0} items</div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Right — menu detail */}
      <div className="col-span-8">
        {!openMenuId && (
          <div className="text-gray-500 text-sm py-8 text-center border rounded">
            Select a menu on the left to manage its items.
          </div>
        )}
        {openMenuId && openMenu && (
          <MenuDetail
            orgId={orgId}
            menu={openMenu}
            onChange={() => { loadMenu(openMenuId, openMenuKind); loadMenus(); }}
            onRename={renameMenu}
            onDelete={deleteMenu}
          />
        )}
      </div>
      </div>
    </div>
  );
}

/**
 * Small reusable header that shows the live WP site URL the user is
 * editing against. Pulls site_url from the bridge config endpoint.
 * Renders a "View live site ↗" link plus a one-liner telling the user
 * exactly which website this panel writes to.
 *
 * NOTE: kept as a backwards-compatible local export — the canonical
 * component is now ./LiveSiteHeader.js so other panels can share it.
 */

/**
 * HeaderAppearance — controls the live site header bar (background colour, nav
 * menu font size + colours, logo image tag + logo size) via the website-settings
 * API. WP reads these through ol_get_header_config() → --ol-header-* CSS vars.
 * Saving also fires /refresh so the live WP site updates on its next check.
 * The bar's vertical HEIGHT is intentionally fixed — the logo auto-fits it.
 */
function HeaderAppearance({ orgId }) {
  const [header, setHeader] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/website-settings/${orgId}`, { withCredentials: true });
        setHeader(data.header || {});
      } catch {
        setError('Could not load header settings.');
      }
    })();
  }, [orgId]);

  const set = (k, v) => { setHeader((h) => ({ ...h, [k]: v })); setSaved(false); };

  const save = async () => {
    if (!header) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const payload = {
        header: {
          bg_color: header.bg_color,
          nav_text_color: header.nav_text_color,
          nav_hover_color: header.nav_hover_color,
          nav_font_size: Number(header.nav_font_size),
          logo_height: Number(header.logo_height),
          logo_media_tag: (header.logo_media_tag || '').trim(),
        },
      };
      await axios.patch(`${API}/website-settings/${orgId}`, payload, { withCredentials: true });
      await axios.post(`${API}/website-settings/${orgId}/refresh`, {}, { withCredentials: true });
      setSaved(true);
    } catch {
      setError('Save failed. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  if (!header) {
    return (
      <div data-testid="header-appearance-loading" className="border rounded-lg p-4 text-sm text-gray-500">
        Loading header settings…
      </div>
    );
  }

  return (
    <div data-testid="header-appearance" className="border rounded-lg bg-white">
      <button
        data-testid="header-appearance-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-base font-semibold">Header Bar Appearance</span>
        <span className="text-gray-400 text-sm">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-5">
          <p className="text-xs text-gray-500 -mt-1">
            Controls the top header bar of the live site. The bar height stays fixed —
            the logo automatically fits it. Changes go live on the next page load.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {/* Background color */}
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-700">Background color</span>
              <span className="flex items-center gap-2">
                <input type="color" data-testid="header-bg-color"
                  value={header.bg_color || '#3d1220'}
                  onChange={(e) => set('bg_color', e.target.value)}
                  className="h-8 w-10 rounded border cursor-pointer" />
                <input type="text" data-testid="header-bg-hex"
                  value={header.bg_color || ''}
                  onChange={(e) => set('bg_color', e.target.value)}
                  className="w-24 border rounded px-2 py-1 text-xs font-mono" />
              </span>
            </label>

            {/* Nav text color */}
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-700">Nav text color</span>
              <span className="flex items-center gap-2">
                <input type="color" data-testid="header-nav-color"
                  value={header.nav_text_color || '#ffffff'}
                  onChange={(e) => set('nav_text_color', e.target.value)}
                  className="h-8 w-10 rounded border cursor-pointer" />
                <input type="text" data-testid="header-nav-color-hex"
                  value={header.nav_text_color || ''}
                  onChange={(e) => set('nav_text_color', e.target.value)}
                  className="w-24 border rounded px-2 py-1 text-xs font-mono" />
              </span>
            </label>

            {/* Nav hover color */}
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-700">Nav hover color</span>
              <span className="flex items-center gap-2">
                <input type="color" data-testid="header-nav-hover-color"
                  value={header.nav_hover_color || '#e87722'}
                  onChange={(e) => set('nav_hover_color', e.target.value)}
                  className="h-8 w-10 rounded border cursor-pointer" />
                <input type="text" data-testid="header-nav-hover-hex"
                  value={header.nav_hover_color || ''}
                  onChange={(e) => set('nav_hover_color', e.target.value)}
                  className="w-24 border rounded px-2 py-1 text-xs font-mono" />
              </span>
            </label>

            {/* Nav font size */}
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-700">Nav menu font size</span>
              <span className="flex items-center gap-2">
                <input type="range" min="10" max="40" step="1"
                  data-testid="header-nav-font-size-range"
                  value={header.nav_font_size ?? 18}
                  onChange={(e) => set('nav_font_size', e.target.value)}
                  className="w-28" />
                <input type="number" min="10" max="40"
                  data-testid="header-nav-font-size"
                  value={header.nav_font_size ?? 18}
                  onChange={(e) => set('nav_font_size', e.target.value)}
                  className="w-16 border rounded px-2 py-1 text-xs" />
                <span className="text-xs text-gray-400">px</span>
              </span>
            </label>

            {/* Logo size */}
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-700">Logo size (height)</span>
              <span className="flex items-center gap-2">
                <input type="range" min="40" max="200" step="1"
                  data-testid="header-logo-height-range"
                  value={header.logo_height ?? 104}
                  onChange={(e) => set('logo_height', e.target.value)}
                  className="w-28" />
                <input type="number" min="40" max="200"
                  data-testid="header-logo-height"
                  value={header.logo_height ?? 104}
                  onChange={(e) => set('logo_height', e.target.value)}
                  className="w-16 border rounded px-2 py-1 text-xs" />
                <span className="text-xs text-gray-400">px</span>
              </span>
            </label>

            {/* Logo media tag */}
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-700">Logo image tag</span>
              <input type="text"
                data-testid="header-logo-media-tag"
                value={header.logo_media_tag || ''}
                onChange={(e) => set('logo_media_tag', e.target.value)}
                placeholder="nav-logo"
                className="w-40 border rounded px-2 py-1 text-xs font-mono" />
            </label>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              data-testid="header-appearance-save"
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
            >{saving ? 'Saving…' : 'Save header'}</button>
            {saved && <span data-testid="header-appearance-saved" className="text-green-600 text-sm">Saved — live site will update shortly.</span>}
            {error && <span data-testid="header-appearance-error" className="text-red-600 text-sm">{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function MenuDetail({ orgId, menu, onChange, onRename, onDelete }) {
  const [name, setName] = useState(menu.name || '');
  const [editing, setEditing] = useState(null);   // item being edited
  const isBlock = menu.kind === 'block';

  useEffect(() => { setName(menu.name || ''); }, [menu]);

  // For block-nav: WP stores items as a serialized block array. The bridge
  // accepts wholesale replacement via PATCH /navigation/{id} {items:[...]},
  // not per-item endpoints. So we compute the new items list client-side
  // and PATCH the menu in one call.
  const writeBlockItems = async (nextItems) => {
    await axios.patch(
      `${API}/wp-menus/${orgId}/${menu.id}?kind=block`,
      { items: nextItems },
      { withCredentials: true },
    );
    onChange();
  };

  const addItem = async () => {
    const title = window.prompt('New item title?');
    if (!title) return;
    const url = window.prompt('Item URL?', '');
    if (isBlock) {
      const next = [...(menu.items || []), { title, url: url || '', type: 'custom' }];
      await writeBlockItems(next);
      return;
    }
    await axios.post(
      `${API}/wp-menus/${orgId}/${menu.id}/items`,
      { title, url: url || '', type: 'custom', object: 'custom' },
      { withCredentials: true },
    );
    onChange();
  };

  const updateItem = async (itemId, patch) => {
    if (isBlock) {
      const next = (menu.items || []).map((it, idx) =>
        (it.id === itemId || idx === itemId) ? { ...it, ...patch } : it,
      );
      await writeBlockItems(next);
      return;
    }
    await axios.patch(
      `${API}/wp-menus/${orgId}/${menu.id}/items/${itemId}`,
      patch,
      { withCredentials: true },
    );
    onChange();
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Delete this menu item?')) return;
    if (isBlock) {
      const next = (menu.items || []).filter((it, idx) =>
        !(it.id === itemId || idx === itemId),
      );
      await writeBlockItems(next);
      return;
    }
    await axios.delete(
      `${API}/wp-menus/${orgId}/${menu.id}/items/${itemId}`,
      { withCredentials: true },
    );
    onChange();
  };

  return (
    <div data-testid="wp-menu-detail">
      <div className="flex items-center justify-between mb-4 gap-3">
        <input
          data-testid="wp-menu-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name !== menu.name && onRename(name)}
          className="flex-1 border-b text-xl font-semibold py-1 px-1 focus:outline-none focus:border-blue-500"
        />
        <button
          data-testid="wp-menu-delete"
          onClick={onDelete}
          className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
        >Delete menu</button>
      </div>

      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium">Items</h3>
        <button
          data-testid="wp-menu-add-item"
          onClick={addItem}
          className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
        >+ Add item</button>
      </div>

      {(menu.items || []).length === 0 && (
        <div className="text-gray-500 text-sm py-4" data-testid="wp-menu-items-empty">
          No items in this menu yet.
        </div>
      )}

      <ul className="divide-y border rounded">
        {(menu.items || []).map((it) => (
          <li key={it.id} className="px-3 py-2" data-testid={`wp-menu-item-${it.id}`}>
            {editing === it.id ? (
              <ItemEditor
                item={it}
                onSave={(patch) => { updateItem(it.id, patch); setEditing(null); }}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{it.title}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {it.url || '(no url)'} · order {it.order}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    data-testid={`wp-menu-item-edit-${it.id}`}
                    onClick={() => setEditing(it.id)}
                    className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                  >Edit</button>
                  <button
                    data-testid={`wp-menu-item-delete-${it.id}`}
                    onClick={() => deleteItem(it.id)}
                    className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                  >×</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ItemEditor({ item, onSave, onCancel }) {
  const [title, setTitle] = useState(item.title || '');
  const [url, setUrl] = useState(item.url || '');
  const [order, setOrder] = useState(item.order || 0);

  const dirty = {};
  if (title !== item.title) dirty.title = title;
  if (url !== (item.url || '')) dirty.url = url;
  if (Number(order) !== Number(item.order || 0)) dirty.order = Number(order);

  return (
    <div className="grid grid-cols-12 gap-2 items-center">
      <input
        data-testid={`wp-menu-item-title-input-${item.id}`}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        className="col-span-4 border rounded px-2 py-1 text-sm"
      />
      <input
        data-testid={`wp-menu-item-url-input-${item.id}`}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…"
        className="col-span-5 border rounded px-2 py-1 text-sm"
      />
      <input
        data-testid={`wp-menu-item-order-input-${item.id}`}
        type="number"
        value={order}
        onChange={(e) => setOrder(e.target.value)}
        className="col-span-1 border rounded px-2 py-1 text-sm"
      />
      <button
        data-testid={`wp-menu-item-save-${item.id}`}
        disabled={Object.keys(dirty).length === 0}
        onClick={() => onSave(dirty)}
        className="col-span-1 px-2 py-1 bg-blue-600 text-white text-xs rounded disabled:opacity-50"
      >Save</button>
      <button
        data-testid={`wp-menu-item-cancel-${item.id}`}
        onClick={onCancel}
        className="col-span-1 px-2 py-1 border text-xs rounded"
      >Cancel</button>
    </div>
  );
}
